import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WorkerBlockerDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Chip, FilterPill, Panel, inputClass } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { useAuth } from "@/auth/auth";
import {
  ALL_PROJECTS,
  ProjectFilterTabs,
  filterByProject,
  projectGroups,
  resolveProjectFilter,
} from "../decisions/project-filter";
import { ConfirmModal, InboxEmptyRow, InboxShell, useInboxToast } from "../inbox/InboxShell";

/**
 * Blokace — **to, co brání dokončení tasku**. Nic jiného.
 *
 * Když seznam poprvé vznikl, leželo v něm 130 otevřených položek a 124 z nich
 * viselo na tasku ve stavu DONE: hlásilo se sem i to, co worker obešel a task
 * přesto dokončil. To má od té doby vlastní druh záznamu (obchvat) a shrnutí
 * pod seznamem — je to podklad k analýze, ne fronta k odbavení.
 *
 * Počty se čtou ze serveru, ne z délky výpisu: ten je omezený a jednou už
 * hlásil „100 otevřených", když jich bylo 130.
 */
export function BlockersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showResolved, setShowResolved] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { toast, showToast } = useInboxToast();

  const { project: rawProjectFilter = ALL_PROJECTS } = useSearch({ from: "/shell/blockers" });

  const query = useQuery({
    queryKey: ["blockers", showResolved],
    queryFn: () => api.blockers({ includeResolved: showResolved, limit: 100 }),
  });

  const workarounds = useQuery({
    queryKey: ["workarounds"],
    queryFn: () => api.workarounds({ limit: 100 }),
  });

  const allItems = query.data?.blockers ?? [];
  const projectFilter = resolveProjectFilter(projectGroups(allItems), rawProjectFilter);
  const items = filterByProject(allItems, projectFilter);
  // Počet se bere ze serveru, ne z délky výpisu — ten je omezený a hlásil
  // „100 otevřených", když jich bylo 130.
  const openTotal = query.data?.total ?? 0;
  const groupTotal = query.data?.groupTotal ?? 0;
  const truncated = groupTotal > allItems.length;

  return (
    <InboxShell
      testId="blockers"
      title={cs.blockers.title}
      kicker="BLOKACE"
      lead={cs.blockers.lead(showResolved ? groupTotal : openTotal)}
      right={
        <div className="flex flex-wrap items-center gap-2">
          <ProjectFilterTabs
            items={allItems}
            value={projectFilter}
            onChange={(key) => {
              void navigate({
                to: "/blockers",
                search: key === ALL_PROJECTS ? {} : { project: key },
              });
              setSelectedId(null);
            }}
            testIdPrefix="blockers"
          />
          <FilterPill
            active={showResolved}
            onClick={() => {
              setShowResolved((v) => !v);
              setSelectedId(null);
            }}
            testId="blockers-toggle-resolved"
          >
            {showResolved ? cs.blockers.hideResolved : cs.blockers.showResolved}
          </FilterPill>
        </div>
      }
      items={items}
      selectedId={selectedId}
      onSelect={setSelectedId}
      emptyList={<InboxEmptyRow title={cs.blockers.emptyTitle} note={cs.blockers.emptyNote} />}
      listFooter={
        <>
          {truncated ? (
            <div
              className="px-[18px] py-3 text-[11px] leading-relaxed"
              style={{
                borderRadius: "var(--radius-card)",
                border: "1px dashed color-mix(in srgb, var(--warning) 35%, transparent)",
                color: "var(--warning)",
              }}
              data-testid="blockers-truncated"
            >
              {cs.blockers.truncated(allItems.length, groupTotal)}
            </div>
          ) : null}
          {workarounds.data && workarounds.data.total > 0 ? (
            <div
              className="px-[18px] py-3 text-[11px] leading-relaxed"
              style={{
                borderRadius: "var(--radius-card)",
                border: "1px solid var(--border-default)",
                color: "var(--text-tertiary)",
              }}
              data-testid="blockers-workarounds"
            >
              <div className="af-label mb-1" style={{ color: "var(--accent)" }}>
                {cs.blockers.workaroundsTitle}
              </div>
              {cs.blockers.workaroundsNote(
                workarounds.data.total,
                workarounds.data.groupTotal,
              )}
              <div className="mt-2 flex flex-col gap-1">
                {workarounds.data.workarounds.slice(0, 5).map((w) => (
                  <div key={w.id} style={{ color: "var(--text-secondary)" }}>
                    <span style={{ color: "var(--warning)" }}>{w.occurrences}×</span> {w.text}
                    {w.whatWouldHelp ? (
                      <span style={{ color: "var(--accent)" }}> → {w.whatWouldHelp}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      }
      emptyDetail={cs.blockers.emptyDetail}
      emptyDetailNote={cs.blockers.emptyDetailNote}
      toast={toast}
      renderRow={(b) => (
        <>
          <div className="af-mono flex justify-between text-[12px] tracking-[0.1em]">
            <span
              className="font-bold"
              style={{ color: b.resolvedAt ? "var(--positive)" : "var(--warning)" }}
            >
              {b.resolvedAt ? cs.blockers.resolvedTag : "OTEVŘENÁ"}
              {b.occurrences > 1 ? (
                <span style={{ color: "var(--critical)" }}> · {cs.blockers.occurrences(b.occurrences)}</span>
              ) : null}
            </span>
            <span style={{ color: "var(--text-tertiary)" }}>{relativeTime(b.createdAt)}</span>
          </div>
          <div className="af-card-title mb-[3px] mt-[5px] leading-snug">{b.text}</div>
          <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {b.projectName} · #{b.taskNumber} · {b.workerName}
          </div>
        </>
      )}
      renderDetail={(b) => (
        <BlockerDetail
          key={b.id}
          blocker={b}
          onResolved={(msg) => {
            void queryClient.invalidateQueries({ queryKey: ["blockers"] });
            void queryClient.invalidateQueries({ queryKey: ["worker-blockers"] });
            showToast(msg);
            setSelectedId(null);
          }}
        />
      )}
    />
  );
}

function BlockerDetail({
  blocker,
  onResolved,
}: {
  blocker: WorkerBlockerDTO;
  onResolved: (msg: string) => void;
}) {
  const { session } = useAuth();
  const [note, setNote] = useState("");
  const [lessonOpen, setLessonOpen] = useState(false);
  const [lessonTitle, setLessonTitle] = useState("");
  const [lessonReadWhen, setLessonReadWhen] = useState("");
  const [lessonBody, setLessonBody] = useState("");
  const [lessonFactory, setLessonFactory] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["blocker", blocker.id],
    queryFn: () => api.blocker(blocker.id),
  });

  const resolveMutation = useMutation({
    mutationFn: () =>
      api.resolveBlocker(blocker.id, {
        note: note.trim(),
        // Poučení se posílá jen celé. Půlka bez `body` by v brainu založila
        // prázdný zápis, který nikdo nenajde ani nepoužije.
        ...(lessonOpen && lessonTitle.trim().length >= 5 && lessonBody.trim().length >= 10
          ? {
              lesson: {
                title: lessonTitle.trim(),
                ...(lessonReadWhen.trim() ? { readWhen: lessonReadWhen.trim() } : {}),
                body: lessonBody.trim(),
                ...(lessonFactory ? { scope: "FACTORY" as const } : {}),
              },
            }
          : {}),
      }),
    onSuccess: (data) => onResolved(cs.blockers.resolvedToast(data.resolvedCount)),
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  const canResolve = session?.user.role !== "CLIENT" && !blocker.resolvedAt;

  return (
    <div className="max-w-[860px]" data-testid="blockers-detail">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="af-mono flex flex-wrap items-center gap-2 text-[11px]">
          <Chip tone={blocker.resolvedAt ? "green" : "amber"}>
            {blocker.resolvedAt ? `✓ ${cs.blockers.resolvedTag}` : "▲ OTEVŘENÁ"}
          </Chip>
          {blocker.occurrences > 1 ? (
            <span title={cs.blockers.occurrencesNote} data-testid="blocker-occurrences">
              <Chip tone="red">{cs.blockers.occurrences(blocker.occurrences)}</Chip>
            </span>
          ) : null}
          <span style={{ color: "var(--text-tertiary)" }}>
            {blocker.projectName} · {relativeTime(blocker.createdAt)}
            {blocker.occurrences > 1
              ? ` · ${cs.blockers.firstReported} ${relativeTime(blocker.firstReportedAt)}`
              : ""}
          </span>
        </div>
        <span className="af-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          BLOKACE #{blocker.id.slice(-5)}
        </span>
      </div>
      <h2
        className="af-display mb-5 mt-2 text-[22px] font-bold leading-tight"
        style={{ color: "var(--text-primary)" }}
      >
        {blocker.text}
      </h2>

      {blocker.occurrences > 1 ? (
        <Panel tone="red" className="mb-3 p-4">
          <div className="af-label mb-1.5" style={{ color: "var(--critical)" }}>
            {cs.blockers.occurrences(blocker.occurrences)}
          </div>
          <div className="text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {cs.blockers.occurrencesNote}
          </div>
        </Panel>
      ) : null}

      <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Panel className="p-4">
          <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
            {cs.blockers.context}
          </div>
          <div className="text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            <Link
              to="/projects/$slug/tasks/$number"
              params={{ slug: blocker.projectSlug, number: String(blocker.taskNumber) }}
              className="block"
            >
              #{blocker.taskNumber} {blocker.taskTitle} →
            </Link>
            <Link to="/sessions/$id" params={{ id: blocker.sessionId }} className="mt-1 block">
              {cs.blockers.openSession}
            </Link>
          </div>
        </Panel>
        <Panel className="p-4">
          <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
            {cs.blockers.reportedBy}
          </div>
          <div className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
            {blocker.workerName} · session {blocker.sessionStatus}
          </div>
          {detail.data?.decisionId ? (
            <div className="mt-2 text-[12px]" style={{ color: "var(--warning)" }}>
              {cs.blockers.linkedDecision}
            </div>
          ) : null}
        </Panel>
      </div>

      {blocker.resolvedAt ? (
        <Panel tone="green" className="mb-3 p-4">
          <div className="af-label mb-1.5" style={{ color: "var(--positive)" }}>
            ✓ {cs.blockers.resolvedTag}
          </div>
          <div className="text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {blocker.resolutionNote ?? "—"}
          </div>
          <div className="mt-1 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {blocker.resolvedBy ?? "?"} · {relativeTime(blocker.resolvedAt)}
          </div>
        </Panel>
      ) : (
        <>
          <Panel className="mb-3 p-4">
            <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
              {cs.blockers.resolveLabel}
            </div>
            <textarea
              className="w-full resize-y bg-transparent px-3 py-2 font-mono text-[11px] outline-none"
              style={{
                borderRadius: "var(--radius-box)",
                border: "1px solid var(--border-default)",
                color: "var(--text-primary)",
                minHeight: 72,
              }}
              placeholder={cs.blockers.resolvePlaceholder}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="blocker-note"
            />
            <div className="mt-2 text-[11px] leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
              {cs.blockers.resolveHint}
            </div>
          </Panel>

          <Panel className="mb-4 p-4">
            <button
              type="button"
              className="af-label cursor-pointer"
              style={{ color: "var(--accent)" }}
              onClick={() => setLessonOpen((v) => !v)}
              data-testid="blocker-lesson-toggle"
            >
              {lessonOpen ? "▾ " : "▸ "}
              {cs.blockers.lessonLabel}
            </button>
            {lessonOpen ? (
              <div className="mt-3 flex flex-col gap-2">
                <input
                  className={inputClass}
                  placeholder={cs.blockers.lessonTitlePlaceholder}
                  maxLength={120}
                  value={lessonTitle}
                  onChange={(e) => setLessonTitle(e.target.value)}
                  data-testid="blocker-lesson-title"
                />
                <input
                  className={inputClass}
                  placeholder={cs.blockers.lessonReadWhenPlaceholder}
                  maxLength={300}
                  value={lessonReadWhen}
                  onChange={(e) => setLessonReadWhen(e.target.value)}
                  data-testid="blocker-lesson-readwhen"
                />
                <textarea
                  className="w-full resize-y bg-transparent px-3 py-2 font-mono text-[11px] outline-none"
                  style={{
                    borderRadius: "var(--radius-box)",
                    border: "1px solid var(--border-default)",
                    color: "var(--text-primary)",
                    minHeight: 72,
                  }}
                  placeholder={cs.blockers.lessonBodyPlaceholder}
                  maxLength={8000}
                  value={lessonBody}
                  onChange={(e) => setLessonBody(e.target.value)}
                  data-testid="blocker-lesson-body"
                />
                <label className="flex items-center gap-2 text-[12px]" style={{ color: "var(--text-secondary)" }}>
                  <input
                    type="checkbox"
                    checked={lessonFactory}
                    onChange={(e) => setLessonFactory(e.target.checked)}
                    data-testid="blocker-lesson-factory"
                  />
                  {cs.blockers.lessonFactoryScope}
                </label>
                <div className="text-[11px] leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
                  {cs.blockers.lessonHint}
                </div>
              </div>
            ) : null}
          </Panel>

          {error ? (
            <div className="mb-2 text-[12px]" style={{ color: "var(--critical)" }}>
              ⚠ {error}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2.5">
            <Btn
              variant="primary"
              size="lg"
              disabled={resolveMutation.isPending || !canResolve || note.trim().length < 3}
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
              data-testid="blocker-resolve"
            >
              {cs.blockers.submit}
            </Btn>
          </div>
          <div className="mt-3 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {note.trim().length < 3 ? cs.blockers.noteRequired : cs.blockers.afterNote}
          </div>

          {confirming ? (
            <ConfirmModal
              testId="blockers"
              kicker={cs.blockers.confirmTitle}
              itemTitle={blocker.text}
              label={
                blocker.occurrences > 1
                  ? cs.blockers.occurrences(blocker.occurrences)
                  : cs.blockers.submit
              }
              body={note.trim()}
              bodyLabel={cs.blockers.resolveLabel}
              pending={resolveMutation.isPending}
              afterNote={cs.blockers.afterNote}
              onConfirm={() => {
                setConfirming(false);
                resolveMutation.mutate();
              }}
              onCancel={() => setConfirming(false)}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
